"use client";

import { useEffect, useRef, useState } from "react";
import { SiteSafeImage } from "@/components/SiteSafeImage";
import { createFbxRenderCore } from "@/lib/render-core/fbx-render-core";
import {
  applyDiscoveredProductGroups,
  applyProductRenderSelection,
  applyRememberedProductRenderSelection,
  fetchProductRenderDraft,
  fetchProductRenderResources,
  normalizeProductRenderDraft,
  PRODUCT_RENDER_OPTIONS_EVENT,
  rememberCurrentProductRenderDraft,
  type ProductRenderPayload,
  type ProductRenderDraft,
  type ProductRenderResourcePayload,
  type ProductRenderSelection,
} from "@/lib/product-render-client";
import styles from "./productDetail.module.css";

const PRODUCT_FIT_AZIMUTH_OVERRIDES: Readonly<Record<string, number>> = {
  // This pendant's default FBX forward axis exposes only its thin edge.
  "10001956": Math.PI / 3,
};

type ProductRenderViewerProps = {
  goodsId: string;
  legacyModelId?: string;
  renderAvailable: boolean;
  fallbackImage: string;
  previewLabel: string;
  imageLabel: string;
  dimensionLabel: string;
  dataLabel: string;
  unavailableLabel: string;
  initialPayload?: ProductRenderPayload | null;
  alt?: string;
};

export function ProductRenderViewer({
  goodsId,
  legacyModelId,
  renderAvailable,
  fallbackImage,
  previewLabel,
  imageLabel,
  dimensionLabel,
  dataLabel,
  unavailableLabel,
  initialPayload,
  alt = "",
}: Readonly<ProductRenderViewerProps>) {
  const initialDraftRef = useRef<ProductRenderDraft | null>(
    applyRememberedProductRenderSelection(initialPayload ? normalizeProductRenderDraft(initialPayload) : null, goodsId),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const coreRef = useRef<ReturnType<typeof createFbxRenderCore> | null>(null);
  const stateRef = useRef<ProductRenderDraft | null>(initialDraftRef.current);
  const resourcesRef = useRef<ProductRenderResourcePayload | null>(null);
  const [draft, setDraft] = useState<ProductRenderDraft | null>(initialDraftRef.current);
  const [showFallback, setShowFallback] = useState(false);
  const [showRender, setShowRender] = useState(false);

  useEffect(() => {
    if (!renderAvailable) return;
    let cancelled = false;
    setShowFallback(false);
    setShowRender(false);
    void fetchProductRenderResources(goodsId, legacyModelId)
      .then((resources) => {
        if (cancelled) return;
        resourcesRef.current = resources;
        if (resources) {
          setDraft((current) => {
            const nextDraft = applyRememberedProductRenderSelection(current, goodsId, resources);
            stateRef.current = nextDraft;
            return nextDraft;
          });
        }
      })
      .catch(() => {
        resourcesRef.current = null;
      });
    void fetchProductRenderDraft(goodsId, legacyModelId)
      .then((nextDraft) => {
        if (cancelled) return;
        if (!nextDraft) {
          if (!stateRef.current) {
            setShowFallback(true);
          }
          return;
        }
        const selectedDraft = applyRememberedProductRenderSelection(nextDraft, goodsId, resourcesRef.current);
        stateRef.current = selectedDraft;
        setDraft(selectedDraft);
      })
      .catch(() => {
        if (!cancelled && !stateRef.current) setShowFallback(true);
      });

    return () => {
      cancelled = true;
    };
  }, [goodsId, legacyModelId, renderAvailable]);

  useEffect(() => {
    stateRef.current = draft;
    rememberCurrentProductRenderDraft(goodsId, draft);
    if (draft) {
      coreRef.current?.update(draft.scene, draft.config);
    }
  }, [draft, goodsId]);

  useEffect(() => {
    return () => rememberCurrentProductRenderDraft(goodsId, null);
  }, [goodsId]);

  useEffect(() => {
    function handleSelection(event: Event) {
      const selection = (event as CustomEvent<ProductRenderSelection>).detail;
      if (!selection || selection.goodsId !== goodsId) {
        return;
      }

      setDraft((current) => {
        if (!current) return current;
        const nextDraft = applyProductRenderSelection(current, selection, resourcesRef.current);
        stateRef.current = nextDraft;
        return nextDraft;
      });
    }

    window.addEventListener(PRODUCT_RENDER_OPTIONS_EVENT, handleSelection);
    return () => window.removeEventListener(PRODUCT_RENDER_OPTIONS_EVENT, handleSelection);
  }, [goodsId]);

  useEffect(() => {
    if (!showRender || !containerRef.current || !draft?.uploadId || showFallback) return;

    const core = createFbxRenderCore(containerRef.current, {
      fitPadding: 2.25,
      fitAzimuth: PRODUCT_FIT_AZIMUTH_OVERRIDES[goodsId] ?? 0,
    });
    coreRef.current = core;
    let cancelled = false;
    const query = new URLSearchParams();
    if (legacyModelId) {
      query.set("legacyModelId", legacyModelId);
    }
    const suffix = query.toString() ? `?${query.toString()}` : "";

    void core.load(`/api/render/persistence/products/${encodeURIComponent(goodsId)}/file${suffix}`, draft.fileName)
      .then((groups) => {
        if (cancelled) return;
        const current = stateRef.current;
        if (!current) return;
        const nextDraft = applyRememberedProductRenderSelection(applyDiscoveredProductGroups(current, groups), goodsId, resourcesRef.current);
        stateRef.current = nextDraft;
        setDraft(nextDraft);
        core.update(nextDraft.scene, nextDraft.config);
      })
      .catch(() => {
        if (!cancelled) setShowFallback(true);
      });

    return () => {
      cancelled = true;
      core.dispose();
      coreRef.current = null;
    };
  }, [draft?.uploadId, goodsId, legacyModelId, showFallback, showRender]);

  const canShowRender = renderAvailable && Boolean(draft && !showFallback);

  return (
    <>
      {showRender && canShowRender ? (
        <div className={styles.productRenderViewer} aria-label={draft?.fileName ?? previewLabel}>
          <div ref={containerRef} className={styles.productRenderCanvas} />
        </div>
      ) : (
        <SiteSafeImage alt={alt} className={styles.heroImage} src={fallbackImage} unavailableLabel={unavailableLabel} />
      )}
      <div className={styles.previewMeta}>
        <span>{dimensionLabel}</span>
        <span>{dataLabel}</span>
      </div>
      <div className={styles.previewSwitch} aria-label={previewLabel}>
        <button
          type="button"
          className={!showRender ? styles.previewThumbActive : styles.previewThumb}
          aria-label={alt || imageLabel}
          aria-pressed={!showRender}
          onClick={() => setShowRender(false)}
        >
          <SiteSafeImage src={fallbackImage} unavailableLabel={unavailableLabel} />
        </button>
        {renderAvailable ? (
          <button
            type="button"
            className={showRender ? styles.previewThumbActive : styles.previewThumb}
            aria-pressed={showRender}
            disabled={!canShowRender}
            onClick={() => setShowRender(true)}
          >
            <span className={styles.previewCube} aria-hidden="true" />
            <span>{previewLabel}</span>
          </button>
        ) : null}
      </div>
    </>
  );
}
